import { ResponseEnum, FeedbackI } from "../types";
import { CreateFeedbackRequest } from "../api/types";

interface LessonCompletionOptions {
  saveAnswers: boolean;
  recapId?: string;
  lessonTitle?: string;
  moduleId: string;
  lessonId?: string;
  hasAnswers: boolean;
  feedback?: FeedbackI | null;
}

interface RecapHandlingResult {
  shouldProceed: boolean;
}

interface ResponseMutationData {
  module: string;
  lesson?: string;
  title: string;
  keyTakeaway?: string;
  type: ResponseEnum;
  content: Record<string, unknown>;
  isCompletion?: boolean;
}

interface RecapContentEntry {
  [key: string]: { value?: unknown };
}

/**
 * Handles the common logic for lesson completion when saving or not saving answers
 */
export async function handleLessonCompletion(
  options: LessonCompletionOptions,
  handlers: {
    handleSaveAndContinue: () => Promise<void>;
    deleteRecapMutation: { mutateAsync: (id: string) => Promise<unknown> };
    createResponseMutation: {
      mutateAsync: (data: ResponseMutationData) => Promise<unknown>;
    };
    feedbackMutation?: {
      mutateAsync: (data: CreateFeedbackRequest) => Promise<unknown>;
    };
    notify: (type: "success" | "error", message: string) => void;
  },
): Promise<RecapHandlingResult> {
  const { saveAnswers, recapId, lessonTitle, moduleId, lessonId, feedback } =
    options;
  const {
    handleSaveAndContinue,
    deleteRecapMutation,
    createResponseMutation,
    feedbackMutation,
    notify,
  } = handlers;

  // Always save feedback if it exists, regardless of saveAnswers setting
  if (feedback && feedbackMutation) {
    const feedbackRequest: CreateFeedbackRequest = {
      lessonId: feedback.lessonId,
      moduleId: feedback.moduleId,
      feedback: feedback.feedback,
      subFeedback: feedback.subFeedback,
      otherFeedback: feedback.otherFeedback,
      createdAt: feedback.createdAt,
      canContact: feedback.canContact,
    };
    // Don't await - let it run in background, mutation handles its own errors
    feedbackMutation.mutateAsync(feedbackRequest).catch(() => {
      // Silently fail - feedback saving shouldn't block lesson completion
    });
  }

  if (saveAnswers) {
    await handleSaveAndContinue();
    return { shouldProceed: true };
  } else {
    // When saveAnswersToLog is false, clear recap but mark lesson as complete
    if (recapId) {
      await deleteRecapMutation.mutateAsync(recapId);
    }

    // Create main lesson recap to mark the lesson as complete (same as when saveAnswersToLog is true but no answers)
    if (lessonTitle) {
      await createResponseMutation.mutateAsync({
        module: moduleId,
        lesson: lessonId,
        title: lessonTitle,
        keyTakeaway: undefined,
        type: ResponseEnum.SECTION_RECAP,
        content: {},
        isCompletion: true,
      });
    }

    // Show completion message - same as when there are no answers to save
    notify("success", "Nice work, you've completed this section!");

    return { shouldProceed: true };
  }
}

/**
 * Determines if the save modal should be shown based on user preferences and whether there are answers to save
 */
export function shouldShowSaveModal(
  askEveryTime: boolean | undefined,
  hasAnswers: boolean = true,
): boolean {
  // Don't show modal if there are no answers to save
  if (!hasAnswers) {
    return false;
  }

  // Default to true (ask every time) if no preference is set
  return askEveryTime ?? true;
}

/**
 * Helper function to check if there are actual answers in the recap content
 */
export function hasAnswersToSave(recapContent: RecapContentEntry[]): boolean {
  if (!recapContent) return false;

  return recapContent.some((entry: RecapContentEntry) =>
    Object.keys(entry).some((key) => entry[key]?.value),
  );
}
